import React, { useState, useEffect } from 'react'
import { useAccount, useBalance } from 'wagmi'
import { formatEther } from 'viem'
import { useContract } from '../hooks/useContract'
import { usePlayerStatus } from '../hooks/useOGBToken'
import { useStaking } from '../hooks/useStaking'
import { X } from 'lucide-react'
import { POTION_DEFINITIONS, PotionTier, PotionEffectType } from '../game/PotionCraftingEngine'
import type { ActivePage } from './MainMenu'

// AGC: Buff type definitions (matching Buff1155.sol)
// AGC: Using PNG images instead of emojis
const BUFF_TYPES = {
  0: { name: 'Shield', image: '/shield.png', description: 'Reduces incoming damage by 25%', color: 'text-blue-400' },
  1: { name: 'Pierce', image: '/pierce.png', description: 'Reduces enemy defense effectiveness', color: 'text-red-400' },
  2: { name: 'Haste', image: '/haste.png', description: 'Adds +5 flat damage before defense', color: 'text-yellow-400' },
  3: { name: 'Frost', image: '/frost.png', description: 'Freezes opponent for one turn', color: 'text-cyan-400' }
} as const

interface InventoryProps {
  playerAddress: string
  onNavigateToPage?: (page: ActivePage) => void
  onNavigateToMarketplace?: () => void
}

// AGC: Potion type definitions (matching ClassicPotion1155.sol)
// AGC: Potion image mapping: red=healing, blue=mana, purple=double strike, green=poison
const POTION_TYPES = {
  0: { name: 'Healing Potion', image: '/potion_red.png', description: 'Instant +10 energy when used', color: 'text-red-400' },
  1: { name: 'Mana Potion', image: '/potion_blue.png', description: '+5% buff power for ALL remaining turns', color: 'text-purple-400' },
  2: { name: 'Double Strike Potion', image: '/potion_purple.png', description: '+25% damage ONLY when you win', color: 'text-yellow-400' },
  3: { name: 'Poison Potion', image: '/potion_green.png', description: '2.5 dmg/turn × 5 turns (12.5 total)', color: 'text-green-400' }
} as const

const RARITY_NAMES = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'] as const

// AGC: Inventory component redesigned to match Profile.tsx style
export const Inventory: React.FC<InventoryProps> = ({ playerAddress, onNavigateToPage, onNavigateToMarketplace }) => {
  const { address } = useAccount()
  const { getPlayerBuffBalances, getPlayerClassicPotions, getPlayerCraftedPotions, getUSDCBalance } = useContract()
  const { balance: ogbBalance } = usePlayerStatus(address || playerAddress as `0x${string}`)
  
  // AGC: Get ETH balance
  const { data: ethBalance } = useBalance({
    address: (address || playerAddress) as `0x${string}`,
  })

  // AGC: Component state
  const [ownedBuffs, setOwnedBuffs] = useState<Record<number, number>>({})
  const [ownedPotions, setOwnedPotions] = useState<Array<{
    tokenId: number
    potionType: number
    rarity: number
    magnitude: number
    balance: number
  }>>([])
  const [ownedCraftedPotions, setOwnedCraftedPotions] = useState<Array<{
    tokenId: number
    potionTypeId: number
    rarity: number
    magnitude: number
    balance: number
  }>>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'buffs' | 'potions' | 'crafted' | 'wallet'>('buffs')
  const [usdcBalance, setUsdcBalance] = useState<string>('0')
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [includeStakingInBalance, setIncludeStakingInBalance] = useState(false) // AGC: Toggle for including staked tokens in wallet balance
  
  // AGC: Get staking info
  const { stakingInfo } = useStaking((address || playerAddress) as `0x${string}`)
  const stakedOGB = stakingInfo?.stakedBalance ? Number(formatEther(stakingInfo.stakedBalance)) : 0

  // AGC: Load USDC balance
  useEffect(() => {
    if (!playerAddress || !getUSDCBalance) return
    
    let isMounted = true
    
    const loadUSDCBalance = async () => {
      try {
        const balance = await getUSDCBalance(playerAddress)
        if (!isMounted) return
        setUsdcBalance(balance || '0')
      } catch (err) {
        if (!isMounted) return
        // AGC: Silently handle USDC balance errors (contract might not be deployed)
        console.warn('Failed to load USDC balance:', err)
        setUsdcBalance('0')
      }
    }
    
    loadUSDCBalance()
    const interval = setInterval(loadUSDCBalance, 30000) // Refresh every 30s
    return () => {
      isMounted = false
      clearInterval(interval)
    }
  }, [playerAddress]) // AGC: Removed getUSDCBalance from deps to prevent infinite loops

  // AGC: Load player's buff inventory
  useEffect(() => {
    if (!playerAddress) return

    let isMounted = true

    const loadInventory = async () => {
      setIsLoading(true)
      setError(null)
      
      try {
        const actualInventoryData = await getPlayerBuffBalances(playerAddress)
        
        if (!isMounted) return
        
        const actualInventory: Record<number, number> = {}
        actualInventoryData.forEach((item: { tokenId: number; balance: number }) => {
          actualInventory[item.tokenId] = item.balance
        })
        setOwnedBuffs(actualInventory)
        
        // AGC: Load classic potions
        if (getPlayerClassicPotions) {
          try {
            const potions = await getPlayerClassicPotions(playerAddress)
            if (!isMounted) return
            setOwnedPotions(potions)
          } catch (err: any) {
            if (!isMounted) return
            if (err?.message && !err.message.includes('404')) {
              console.warn('⚠️ Failed to load classic potions:', err.message)
            }
            setOwnedPotions([])
          }
        }
        
        // AGC: Load crafted potions
        if (getPlayerCraftedPotions) {
          try {
            const craftedPotions = await getPlayerCraftedPotions(playerAddress)
            if (!isMounted) return
            setOwnedCraftedPotions(craftedPotions)
          } catch (err: any) {
            if (!isMounted) return
            if (err?.message && !err.message.includes('404')) {
              console.warn('⚠️ Failed to load crafted potions:', err.message)
            }
            setOwnedCraftedPotions([])
          }
        }
        
      } catch (err) {
        if (!isMounted) return
        console.error('Failed to load inventory:', err)
        setError('Failed to load your buff inventory.')
        setOwnedBuffs({})
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadInventory()

    return () => {
      isMounted = false
    }
  }, [playerAddress]) // AGC: Removed function dependencies to prevent infinite loops - functions from useContract are stable

  // AGC: Get total buff count
  const getTotalBuffs = () => {
    return Object.values(ownedBuffs).reduce((sum, count) => sum + count, 0)
  }

  // AGC: Get owned buffs only (count > 0)
  const getOwnedBuffsList = () => {
    return Object.entries(ownedBuffs)
      .filter(([_, count]) => count > 0)
      .map(([id, count]) => ({ id: parseInt(id), count }))
      .sort((a, b) => a.id - b.id)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white text-sm">
        <div className="text-slate-300 animate-pulse">Loading inventory…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-white flex flex-col gap-3">
        <div className="text-center py-12">
          <div className="text-red-400 text-xl mb-4">❌ {error}</div>
          <p className="text-slate-400 text-sm">Please try refreshing the page</p>
        </div>
      </div>
    )
  }

  const ownedBuffsList = getOwnedBuffsList()
  const totalBuffs = getTotalBuffs()
  const totalPotions = ownedPotions.reduce((sum, p) => sum + p.balance, 0)
  const totalCraftedPotions = ownedCraftedPotions.reduce((sum, p) => sum + p.balance, 0)
  const walletShort = `${playerAddress.slice(0, 4)}…${playerAddress.slice(-4)}`

  // AGC: Format balances
  const ethFormatted = ethBalance ? parseFloat(formatEther(ethBalance.value)).toFixed(4) : '0.0000'
  const usdcFormatted = parseFloat(usdcBalance || '0').toFixed(2)
  // AGC: Include staked balance in OGB if setting is enabled
  const totalOGB = includeStakingInBalance ? ogbBalance + stakedOGB : ogbBalance
  const ogbFormatted = totalOGB.toFixed(2)

  return (
    <div className="text-white flex flex-col gap-1.5">
      {/* AGC: Header section - matching Profile.tsx style */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">INVENTORY</p>
            <h1 className="text-2xl font-bold leading-tight text-white">Collection</h1>
            <p className="text-[11px] text-slate-400">{walletShort}</p>
          </div>
          {/* AGC: Settings button - matching TopNav style */}
          <button
            onClick={() => setShowSettingsModal(true)}
            className="hover:opacity-70 transition-opacity"
            aria-label="Open settings"
          >
            <img src={`${import.meta.env.BASE_URL}setting.Png`} alt="Settings" className="w-7 h-7 object-contain" />
          </button>
        </div>
      </section>

      {/* AGC: Tab Navigation - Marketplace style */}
      {/* AGC: FIX - Full width grey area, not rounded, show outside borders for horizontal scroll */}
      <section className="pb-2 border-b border-white/10">
        <div className="overflow-x-auto scrollbar-hide relative z-0 -mx-3">
          <div className="inline-flex bg-slate-800/80 p-1 min-w-full border-0 relative">
            <button
              onClick={() => setActiveTab('buffs')}
              className={`min-h-[40px] px-4 py-2 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap flex-1 cursor-pointer pointer-events-auto relative z-10 ${
                activeTab === 'buffs'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg shadow-lg shadow-blue-500/30'
                  : 'text-slate-300 hover:text-white active:bg-slate-700/50 rounded-lg'
              }`}
            >
              BUFF
            </button>
            <button
              onClick={() => setActiveTab('potions')}
              className={`min-h-[40px] px-4 py-2 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap flex-1 cursor-pointer pointer-events-auto relative z-10 ${
                activeTab === 'potions'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-lg shadow-purple-500/30'
                  : 'text-slate-300 hover:text-white active:bg-slate-700/50 rounded-lg'
              }`}
            >
              POTIONS
            </button>
            <button
              onClick={() => setActiveTab('crafted')}
              className={`min-h-[40px] px-4 py-2 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap flex-1 cursor-pointer pointer-events-auto relative z-10 ${
                activeTab === 'crafted'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-lg shadow-purple-500/30'
                  : 'text-slate-300 hover:text-white active:bg-slate-700/50 rounded-lg'
              }`}
            >
              CRAFTED
            </button>
            <button
              onClick={() => setActiveTab('wallet')}
              className={`min-h-[40px] px-4 py-2 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap flex-1 cursor-pointer pointer-events-auto relative z-10 ${
                activeTab === 'wallet'
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg shadow-lg shadow-green-500/30'
                  : 'text-slate-300 hover:text-white active:bg-slate-700/50 rounded-lg'
              }`}
            >
              WALLET
            </button>
          </div>
        </div>
      </section>

      {/* AGC: Stats Grid - matching Profile.tsx style */}
      {activeTab === 'buffs' && (
        <section className="grid grid-cols-2 gap-1.5 text-[11px]">
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>🎒</span>
              Total
            </div>
            <div className="text-2xl font-extrabold mt-0.5 text-sky-300">{totalBuffs}</div>
            <div className="text-[9px] text-slate-500">Buffs</div>
          </div>
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>⚔️</span>
              Types
            </div>
            <div className="text-2xl font-extrabold mt-0.5 text-emerald-300">{ownedBuffsList.length}</div>
            <div className="text-[9px] text-slate-500">Unique</div>
          </div>
        </section>
      )}

      {activeTab === 'potions' && (
        <section className="grid grid-cols-2 gap-1.5 text-[11px]">
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>🧪</span>
              Total
            </div>
            <div className="text-2xl font-extrabold mt-0.5 text-purple-300">{totalPotions}</div>
            <div className="text-[9px] text-slate-500">Potions</div>
          </div>
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>⚔️</span>
              Types
            </div>
            <div className="text-2xl font-extrabold mt-0.5 text-emerald-300">{ownedPotions.length}</div>
            <div className="text-[9px] text-slate-500">Unique</div>
          </div>
        </section>
      )}

      {activeTab === 'crafted' && (
        <section className="grid grid-cols-2 gap-1.5 text-[11px]">
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>🔮</span>
              Total
            </div>
            <div className="text-2xl font-extrabold mt-0.5 text-purple-300">{totalCraftedPotions}</div>
            <div className="text-[9px] text-slate-500">Crafted</div>
          </div>
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>⚔️</span>
              Types
            </div>
            <div className="text-2xl font-extrabold mt-0.5 text-emerald-300">{ownedCraftedPotions.length}</div>
            <div className="text-[9px] text-slate-500">Unique</div>
          </div>
        </section>
      )}

      {activeTab === 'wallet' && (
        <section className="grid grid-cols-3 gap-1.5 text-[11px]">
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>💎</span>
              ETH
            </div>
            <div className="text-xl font-extrabold mt-0.5 text-cyan-300">{ethFormatted}</div>
            <div className="text-[9px] text-slate-500">Balance</div>
          </div>
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>💵</span>
              USDC
            </div>
            <div className="text-xl font-extrabold mt-0.5 text-blue-300">{usdcFormatted}</div>
            <div className="text-[9px] text-slate-500">Balance</div>
          </div>
          <div className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)]">
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>⚡</span>
              OGB
            </div>
            <div className="text-xl font-extrabold mt-0.5 text-yellow-300">{ogbFormatted}</div>
            <div className="text-[9px] text-slate-500">
              {includeStakingInBalance && stakedOGB > 0 ? 'Total' : 'Balance'}
            </div>
            {includeStakingInBalance && stakedOGB > 0 && (
              <div className="text-[8px] text-slate-600 mt-0.5">
                +{stakedOGB.toFixed(2)} staked
              </div>
            )}
          </div>
        </section>
      )}

      {/* AGC: Content Section */}
      <section className="flex-1 overflow-y-auto">
        {activeTab === 'buffs' && (
          <>
            {ownedBuffsList.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {ownedBuffsList.map(({ id, count }) => {
                  const buff = BUFF_TYPES[id as keyof typeof BUFF_TYPES]
                  if (!buff) return null

                  return (
                    <div key={id} className="bg-slate-900/60 rounded-xl p-3 border-2 border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <img src={buff.image} alt={buff.name} className="w-10 h-10 object-contain drop-shadow-lg" />
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-sm font-bold ${buff.color} truncate`}>{buff.name}</h3>
                          <p className="text-[9px] text-slate-400 truncate">{buff.description}</p>
                        </div>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Owned:</span>
                          <span className="text-white font-bold">{count}</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🎒</div>
                <h2 className="text-lg font-bold text-white mb-2">No buffs yet</h2>
                <p className="text-slate-400 text-sm mb-4">Purchase buff NFTs from the marketplace</p>
                <button
                  onClick={() => {
                    if (onNavigateToMarketplace) onNavigateToMarketplace()
                    else if (onNavigateToPage) onNavigateToPage('market')
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition"
                >
                  Go to Marketplace
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'potions' && (
          <>
            {ownedPotions.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {ownedPotions.map((potion) => {
                  const potionInfo = POTION_TYPES[potion.potionType as keyof typeof POTION_TYPES]
                  if (!potionInfo) return null

                  const rarityName = RARITY_NAMES[potion.rarity] || 'Unknown'
                  const rarityColors = {
                    0: 'border-white',
                    1: 'border-green-500',
                    2: 'border-blue-500',
                    3: 'border-purple-500',
                    4: 'border-orange-500'
                  }
                  const borderColor = rarityColors[potion.rarity as keyof typeof rarityColors] || 'border-white'

                  return (
                    <div key={potion.tokenId} className={`bg-slate-900/60 rounded-xl p-3 border-2 ${borderColor}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <img 
                          src={potionInfo.image || '/potion_red.png'} 
                          alt={potionInfo.name}
                          className="w-10 h-10 object-contain"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-sm font-bold ${potionInfo.color} truncate`}>{potionInfo.name}</h3>
                          <p className="text-[9px] text-slate-400 truncate">{potionInfo.description}</p>
                        </div>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Owned:</span>
                          <span className="text-white font-bold">{potion.balance}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Rarity:</span>
                          <span className={`font-semibold ${borderColor.replace('border-', 'text-')}`}>{rarityName}</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🧪</div>
                <h2 className="text-lg font-bold text-white mb-2">No potions yet</h2>
                <p className="text-slate-400 text-sm mb-4">Mint potions from the marketplace</p>
                <button
                  onClick={() => {
                    if (onNavigateToMarketplace) onNavigateToMarketplace()
                    else if (onNavigateToPage) onNavigateToPage('market')
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition"
                >
                  Go to Marketplace
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'crafted' && (
          <>
            {ownedCraftedPotions.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {ownedCraftedPotions.map((potion) => {
                  // AGC: Map potionTypeId (0-17) to potion definition
                  const potionDef = POTION_DEFINITIONS[potion.potionTypeId]
                  if (!potionDef) return null

                  // AGC: Get tier name for image
                  const tierName = potionDef.tier === PotionTier.Minor ? 'minor' :
                                  potionDef.tier === PotionTier.Large ? 'larger' :
                                  potionDef.tier === PotionTier.Greater ? 'greater' :
                                  'major'
                  
                  // AGC: Get effect color for tier image
                  const getEffectColor = (effectType: PotionEffectType): string => {
                    switch (effectType) {
                      case PotionEffectType.Heal:
                      case PotionEffectType.Shield:
                        return 'red'
                      case PotionEffectType.Pierce:
                      case PotionEffectType.DoubleStrike:
                        return 'purple'
                      case PotionEffectType.Poison:
                      case PotionEffectType.Haste:
                        return 'green'
                      case PotionEffectType.ManaRegen:
                      case PotionEffectType.Frost:
                        return 'blue'
                      default:
                        return 'green'
                    }
                  }
                  
                  const color = getEffectColor(potionDef.effect.type)
                  const tierImagePath = `/${tierName}_${color}.png`
                  
                  // AGC: Get effect icon
                  const getEffectIcon = (effectType: PotionEffectType): string => {
                    switch (effectType) {
                      case PotionEffectType.Shield:
                        return '/shield.png'
                      case PotionEffectType.Pierce:
                        return '/pierce.png'
                      case PotionEffectType.Haste:
                        return '/haste.png'
                      case PotionEffectType.Frost:
                        return '/frost.png'
                      case PotionEffectType.Heal:
                        return '/potion_red.png'
                      case PotionEffectType.ManaRegen:
                        return '/potion_blue.png'
                      case PotionEffectType.DoubleStrike:
                        return '/pierce.png'
                      case PotionEffectType.Poison:
                        return '/potion_green.png'
                      default:
                        return '/haste.png'
                    }
                  }
                  
                  const effectIconPath = getEffectIcon(potionDef.effect.type)
                  const rarityName = RARITY_NAMES[potion.rarity] || 'Unknown'
                  const rarityColors = {
                    0: 'border-white',
                    1: 'border-green-500',
                    2: 'border-blue-500',
                    3: 'border-purple-500',
                    4: 'border-orange-500'
                  }
                  const borderColor = rarityColors[potion.rarity as keyof typeof rarityColors] || 'border-white'

                  return (
                    <div key={potion.tokenId} className={`bg-slate-900/60 rounded-xl p-3 border-2 ${borderColor}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                          <img 
                            src={tierImagePath} 
                            alt={potionDef.displayName}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              const altPath = `/${tierName}_.${color}.png`
                              if (e.currentTarget.src !== altPath) {
                                e.currentTarget.src = altPath
                              }
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-purple-300 truncate">{potionDef.displayName}</h3>
                          <p className="text-[9px] text-slate-400 truncate">Token #{potion.tokenId}</p>
                        </div>
                        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                          <img 
                            src={effectIconPath} 
                            alt="Effect"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Owned:</span>
                          <span className="text-white font-bold">{potion.balance}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Rarity:</span>
                          <span className={`font-semibold ${borderColor.replace('border-', 'text-')}`}>{rarityName}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-400">Magnitude:</span>
                          <span className="text-white font-bold">+{(potion.magnitude / 100).toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🔮</div>
                <h2 className="text-lg font-bold text-white mb-2">No crafted potions yet</h2>
                <p className="text-slate-400 text-sm mb-4">Craft potions using materials and OGB</p>
                <button
                  onClick={() => {
                    if (onNavigateToMarketplace) onNavigateToMarketplace()
                    else if (onNavigateToPage) onNavigateToPage('market')
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition"
                >
                  Go to Marketplace
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'wallet' && (
          <div className="space-y-2">
            <div className="bg-slate-900/60 rounded-xl p-4 border border-white/5">
              <h3 className="text-sm font-bold text-white mb-3">Wallet Funds</h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">ETH:</span>
                  <span className="text-cyan-300 font-bold">{ethFormatted}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">USDC:</span>
                  <span className="text-blue-300 font-bold">{usdcFormatted}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">OGB:</span>
                  <div className="text-right">
                    <span className="text-yellow-300 font-bold">{ogbFormatted}</span>
                    {includeStakingInBalance && stakedOGB > 0 && (
                      <div className="text-[9px] text-slate-500">
                        ({ogbBalance.toFixed(2)} wallet + {stakedOGB.toFixed(2)} staked)
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <a 
              href={`https://sepolia.basescan.org/address/${playerAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center text-[10px] px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            >
              View on BaseScan
            </a>
          </div>
        )}
      </section>

      {/* AGC: Settings Modal */}
      {showSettingsModal && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowSettingsModal(false)}
        >
          <div 
            className="w-full max-w-sm bg-slate-950 border border-blue-500/40 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* AGC: Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-slate-900">
              <h3 className="text-lg font-bold text-white">Inventory Settings</h3>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="p-1 rounded-full bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
                aria-label="Close settings"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* AGC: Modal Content */}
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="text-sm font-semibold text-white">
                    Add staking on my wallet balance
                  </label>
                  <p className="text-xs text-slate-400 mt-1">
                    Include staked OGB tokens in wallet balance display
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeStakingInBalance}
                    onChange={(e) => setIncludeStakingInBalance(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              {includeStakingInBalance && stakedOGB > 0 && (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-200">
                  <p className="font-semibold mb-1">💡 Balance Calculation:</p>
                  <p>Wallet: {ogbBalance.toFixed(2)} OGB</p>
                  <p>Staked: {stakedOGB.toFixed(2)} OGB</p>
                  <p className="mt-1 font-bold">Total: {totalOGB.toFixed(2)} OGB</p>
                </div>
              )}
            </div>
            
            {/* AGC: Modal Footer */}
            <div className="px-4 py-3 border-t border-white/10 bg-slate-900">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg font-semibold hover:from-blue-400 hover:to-purple-400 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
